import React, { FC } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";

import AdvertiserAvatar from "../../components/redesign/elements/AdvertiserAvatar";
import { AdminAdvertiserListItem } from "../../types/advertiserMetricsTypes";

type AdminAdvertisersTableProps = {
  rows: AdminAdvertiserListItem[];
  onRowClick: (advertiserId: string) => void;
};

const TableWrap = styled("div")({
  marginBottom: 8,
});

const TableBox = styled("div")(({ theme }: { theme: Theme }) => ({
  background: theme.palette.brand.white,
  borderRadius: 10,
  border: `1px solid ${theme.palette.brand.gray200}`,
  overflowX: "auto",
}));

const Table = styled("table")({
  width: "100%",
  minWidth: 720,
  borderCollapse: "collapse",
  fontSize: 13,
});

const Thead = styled("thead")(({ theme }: { theme: Theme }) => ({
  "& th": {
    padding: "12px 16px",
    fontSize: 10,
    fontWeight: 600,
    color: theme.palette.brand.gray500,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    background: theme.palette.brand.gray50,
    borderBottom: `1px solid ${theme.palette.brand.gray200}`,
    textAlign: "left",
  },
  "& th.alignRight": {
    textAlign: "right",
  },
}));

const Tbody = styled("tbody")(({ theme }: { theme: Theme }) => ({
  "& tr": {
    borderBottom: `1px solid ${theme.palette.brand.gray100}`,
    cursor: "pointer",
    transition: "background 0.15s",
    "&:last-child": {
      borderBottom: "none",
    },
    "&:hover": {
      background: theme.palette.brand.gray50,
    },
  },
  "& td": {
    padding: "14px 16px",
    color: theme.palette.brand.gray700,
  },
  "& td.alignRight": {
    textAlign: "right",
  },
}));

const AdvertiserCell = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 10,
});

const NameText = styled("span")(({ theme }: { theme: Theme }) => ({
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const SecondaryText = styled("span")(({ theme }: { theme: Theme }) => ({
  color: theme.palette.brand.gray500,
}));

const NumericPrimary = styled("span")(({ theme }: { theme: Theme }) => ({
  fontWeight: 600,
  color: theme.palette.brand.gray900,
}));

const ArchivedBadge = styled("span")(({ theme }: { theme: Theme }) => ({
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  padding: "2px 6px",
  borderRadius: 4,
  background: theme.palette.brand.gray100,
  color: theme.palette.brand.gray500,
  marginLeft: 6,
}));

const EmptyState = styled("div")(({ theme }: { theme: Theme }) => ({
  padding: "40px 24px",
  textAlign: "center",
  color: theme.palette.brand.gray500,
  fontSize: 13,
}));

const formatDomain = (domain?: string): string => {
  const trimmed = domain?.trim();
  return trimmed || "—";
};

const AdminAdvertisersTable: FC<AdminAdvertisersTableProps> = ({ rows, onRowClick }) => {
  if (!rows.length) {
    return (
      <TableWrap>
        <TableBox>
          <EmptyState>No advertisers match the current filters.</EmptyState>
        </TableBox>
      </TableWrap>
    );
  }

  return (
    <TableWrap>
      <TableBox>
        <Table>
          <Thead>
            <tr>
              <th>Advertiser</th>
              <th>Agency</th>
              <th>Domain</th>
              <th className="alignRight">Campaigns</th>
            </tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <tr key={row.brandgenAdvertiserId} onClick={() => onRowClick(row.brandgenAdvertiserId)}>
                <td>
                  <AdvertiserCell>
                    <AdvertiserAvatar name={row.name} />
                    <NameText>{row.name}</NameText>
                    {row.archived ? <ArchivedBadge>Archived</ArchivedBadge> : null}
                  </AdvertiserCell>
                </td>
                <td>
                  <SecondaryText>{row.agencyName || "—"}</SecondaryText>
                </td>
                <td>
                  <SecondaryText>{formatDomain(row.domain)}</SecondaryText>
                </td>
                <td className="alignRight">
                  <NumericPrimary>{row.campaignCount}</NumericPrimary>
                </td>
              </tr>
            ))}
          </Tbody>
        </Table>
      </TableBox>
    </TableWrap>
  );
};

export default AdminAdvertisersTable;
